import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, ShieldCheck, Lock } from "lucide-react";

/**
 * PartnerLogin – eigenständige Login-Seite für den HPS-Anlagencheck.
 * Gestaltet als geschützter Partnerbereich. Nach erfolgreichem Login
 * werden ``partner_hps``-User automatisch nach ``/app/hps-anlagencheck``
 * geleitet.
 */
export default function PartnerLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.role === "partner_hps" || user.role === "admin") {
      navigate("/app/hps-anlagencheck", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.role !== "partner_hps" && u?.role !== "admin") {
        toast.error("Dieser Login ist nur für HPS-Partner. Bitte nutze den TGAone-Login.");
        return;
      }
      toast.success("Willkommen im HPS-Anlagencheck");
      navigate("/app/hps-anlagencheck", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-['Inter',system-ui,sans-serif] relative overflow-hidden"
         style={{ background: "linear-gradient(135deg, #0a1628 0%, #1e3a5f 60%, #0d2b4d 100%)" }}>
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #4a9eff 0%, transparent 40%), radial-gradient(circle at 80% 80%, #2563eb 0%, transparent 40%)" }} />

      {/* Top bar */}
      <header className="relative z-10 px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
          <span className="font-semibold tracking-wide">TGAone</span>
          <span className="text-white/40">·</span>
          <span className="text-sm text-white/60">Partnerplattform</span>
        </Link>
        <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="partnerlogin-back-home">
          ← zurück zur Startseite
        </Link>
      </header>

      {/* Center card */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl backdrop-blur-xl border border-white/10 shadow-2xl p-8"
               style={{ background: "rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg, #4a9eff, #2563eb)" }}>
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">HPS Anlagencheck</h1>
                <p className="text-xs text-white/60">Geschützter Partnerbereich</p>
              </div>
            </div>

            <p className="text-sm text-white/70 mb-6 leading-relaxed">
              Geschützter Testbereich für HPS. Die Anwendung dient der strukturierten
              Anlagenaufnahme, KI-unterstützten Bewertung und Vorbereitung von
              Sanierungsvorschlägen.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1.5">E-Mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="partnerlogin-email"
                  placeholder="info@hps-services.de"
                  className="w-full rounded-lg bg-white/5 border border-white/15 px-3.5 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-blue-400 focus:bg-white/10 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1.5">Passwort</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    data-testid="partnerlogin-password"
                    className="w-full rounded-lg bg-white/5 border border-white/15 px-3.5 py-2.5 pr-10 text-white placeholder-white/30 focus:outline-none focus:border-blue-400 focus:bg-white/10 transition-all"
                  />
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                          tabIndex={-1}>
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="partnerlogin-submit"
                      className="w-full mt-2 rounded-lg py-2.5 px-4 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #4a9eff, #2563eb)" }}>
                {loading ? "Wird angemeldet …" : (<>Login <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-2 text-[11px] text-white/50">
              <Lock className="w-3.5 h-3.5" />
              <span>Pilotversion für Anlagenaufnahme, KI-Bewertung und Sanierungsvorschläge</span>
            </div>
          </div>

          <p className="mt-5 text-center text-xs text-white/40">
            Technische Plattform: <span className="text-white/70">TGAone</span> &nbsp;·&nbsp;
            Partnerbereich: <span className="text-white/70">HPS</span>
          </p>
        </div>
      </main>
    </div>
  );
}
